import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useDashboardQuery } from './useDashboard';
import { Skeleton, EmptyState, StatusTag, type StatusConfig } from '@/components/ui';

// EAA-1845: these lookups map a backend code to an i18n KEY (not the display
// text itself) — the display text is resolved with `t()` at render time,
// inside the component, so the label follows the active locale.
const ACCOUNT_ACTION_KEYS: Record<string, string> = {
    create: 'home.recentActivity.actionCreate',
    delete: 'home.recentActivity.actionDelete',
    reset: 'home.recentActivity.actionReset',
};

const SOURCE_LABEL_KEYS: Record<'account' | 'admin', string> = {
    account: 'home.recentActivity.sourceAccount',
    admin: 'home.recentActivity.sourceAdmin',
};

function buildResultStatusConfig(t: TFunction): Record<string, StatusConfig> {
    return {
        success: { label: t('home.recentActivity.resultSuccess'), tone: 'success' },
        failure: { label: t('home.recentActivity.resultFailure'), tone: 'error' },
    };
}

/**
 * EAA-1814 (S3 rebuild — canonical frame 09): "Nhật ký gần đây" (frame 09
 * lines 991-1266), now reading `recent_activity[]` straight off `GET
 * /dashboard` (EAA-1829) instead of the S2 pass's two separate
 * `fetchAccountAudit` + `fetchAdminAudit` calls merged client-side —
 * `DashboardService._recent_activity` does that exact merge server-side now
 * (capped at 20, newest-first) — see `useDashboard.ts`.
 *
 * NOT BUILT: the frame's full data-table chrome — row checkboxes, a "Cột"
 * column-picker, a search box, and "1 / 218 bản ghi" pagination (frame lines
 * 999-1266). `recent_activity` is an explicitly capped preview (service
 * docstring: "the ticket's optional 'merged audit feed'"), not a paginated
 * endpoint — it has no `count`/`page` fields to page over and no filter
 * params to search with. Building that chrome here would be exactly the
 * "control that changes nothing" the ticket says not to ship. A link to the
 * full `/console/audit` page (which owns real pagination/filtering) replaces
 * it instead.
 */
export function HomeRecentActivity({
    can,
    apiBase,
    accessToken,
    authenticated,
}: {
    can: (key: string) => boolean;
    apiBase: string;
    accessToken: string | null;
    authenticated: boolean;
}) {
    const { t } = useTranslation();
    const canAudit = can('console.audit.view');
    const baseEnabled = authenticated && Boolean(accessToken);
    const dashboardQuery = useDashboardQuery(apiBase, accessToken, baseEnabled && canAudit);

    if (!canAudit) {
        return null;
    }

    const rows = dashboardQuery.data?.recent_activity ?? [];
    const loading = dashboardQuery.isLoading;
    const hasError = dashboardQuery.isError;
    const resultStatusConfig = buildResultStatusConfig(t);

    return (
        <div className="portal-home-card portal-home-activity-card">
            <div className="portal-home-card-head">
                <div className="portal-home-card-head-text">
                    <h3>{t('home.recentActivity.title')}</h3>
                </div>
                <Link to="/console/audit" className="portal-btn portal-btn-sm">
                    {t('home.recentActivity.viewAllCta')}
                </Link>
            </div>

            {loading && (
                <div className="portal-home-activity-skeleton" role="status" aria-live="polite">
                    <span className="portal-sr-only">{t('home.loadingSr')}</span>
                    <Skeleton height="2.5rem" />
                    <Skeleton height="2.5rem" />
                    <Skeleton height="2.5rem" />
                </div>
            )}

            {!loading && hasError && (
                <p className="portal-admin-error">
                    {t('home.recentActivity.loadError')}{' '}
                    <button
                        type="button"
                        className="portal-home-kpi-retry"
                        onClick={() => dashboardQuery.refetch()}
                    >
                        {t('home.retry')}
                    </button>
                </p>
            )}

            {!loading && !hasError && rows.length === 0 && (
                <EmptyState title={t('home.recentActivity.emptyTitle')} />
            )}

            {!loading && !hasError && rows.length > 0 && (
                <div className="portal-table-scroll">
                    <table className="portal-admin-table">
                        <thead>
                            <tr>
                                <th>{t('home.recentActivity.columnTime')}</th>
                                <th>{t('home.recentActivity.columnSource')}</th>
                                {/* EAA-1845: "Hành động" matches common.actions verbatim — reuse
                                    the shared key instead of minting a duplicate. */}
                                <th>{t('common.actions')}</th>
                                <th>{t('home.recentActivity.columnTarget')}</th>
                                <th>{t('home.recentActivity.columnResult')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const actionKey = ACCOUNT_ACTION_KEYS[row.action];
                                return (
                                    <tr key={`${row.source}-${row.id}`}>
                                        <td>{new Date(row.created_at).toLocaleString('vi-VN')}</td>
                                        <td>{t(SOURCE_LABEL_KEYS[row.source])}</td>
                                        <td>
                                            {row.source === 'account' ? (
                                                actionKey ? (
                                                    t(actionKey)
                                                ) : (
                                                    row.action
                                                )
                                            ) : (
                                                <code>{row.action}</code>
                                            )}
                                        </td>
                                        <td>{row.target}</td>
                                        <td>
                                            <StatusTag
                                                status={row.success ? 'success' : 'failure'}
                                                config={resultStatusConfig}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default HomeRecentActivity;
